/**
 * F1.1 — közös pénz- / dátumformázás (UX 13.3).
 * Pénz: ezres szóköz, tizedesvessző, mindig „€”. Dátum: ÉÉÉÉ. HH. NN.
 */

export type FormatMoneyOptions = {
  currency?: string;
  decimals?: number;
};

function finiteNumber(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/** `5870.81` → `5 870,81 €` */
export function formatMoney(
  value: number | null | undefined,
  options: FormatMoneyOptions = {}
): string {
  const decimals = options.decimals ?? 2;
  const n = finiteNumber(value);
  const fixed = Math.abs(n).toFixed(decimals);
  const [intRaw, frac = ""] = fixed.split(".");
  // Node/ICU hu-HU néha nem tesz ezres szeparátort 4 jegyű számokra — kézi szóköz.
  const intGrouped = (intRaw ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const sign = n < 0 ? "-" : "";
  const body = decimals > 0 ? `${sign}${intGrouped},${frac}` : `${sign}${intGrouped}`;
  // currency param reserved (F2 FX); megjelenítés mindig €
  return `${body} €`;
}

function toDateParts(value: string | Date | null | undefined): { y: string; m: string; d: string } | null {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return { y: m[1]!, m: m[2]!, d: m[3]! };
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return {
      y: String(parsed.getFullYear()),
      m: String(parsed.getMonth() + 1).padStart(2, "0"),
      d: String(parsed.getDate()).padStart(2, "0")
    };
  }
  if (Number.isNaN(value.getTime())) return null;
  return {
    y: String(value.getFullYear()),
    m: String(value.getMonth() + 1).padStart(2, "0"),
    d: String(value.getDate()).padStart(2, "0")
  };
}

/** `2026-10-09` → `2026. 10. 09.` */
export function formatDate(value: string | Date | null | undefined): string {
  const p = toDateParts(value);
  if (!p) return "–";
  return `${p.y}. ${p.m}. ${p.d}.`;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "–";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "–";
  const date = formatDate(d);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${date} ${hh}:${mm}`;
}

export function formatPercent(value: number | null | undefined, decimals = 0): string {
  const n = finiteNumber(value);
  const body = new Intl.NumberFormat("hu-HU", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
    .format(n)
    .replace(/\u00a0/g, " ");
  return `${body} %`;
}

export function formatQuantity(value: number | null | undefined, unit?: string | null): string {
  if (value == null || !Number.isFinite(value)) return "–";
  const body = new Intl.NumberFormat("hu-HU", {
    maximumFractionDigits: 3
  })
    .format(value)
    .replace(/\u00a0/g, " ");
  const u = unit?.trim();
  return u ? `${body} ${u}` : body;
}

/** Ragozás: „€-val” (ne „EUR -val”). */
export function withEuroInstrumental(phraseBefore: string): string {
  return `${phraseBefore} €-val`;
}
